import React, { useState } from "react";
import { Loader2, Play, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ModulePage";
import StatusBadge from "@/components/StatusBadge";
import ReadAloud from "@/components/ReadAloud";

const MODELS = [{ v: "claude-sonnet-4-6", l: "Claude Sonnet 4.6" }, { v: "gpt-5.4", l: "GPT-5.4" }, { v: "gemini-3.1-pro", l: "Gemini 3.1 Pro" }];

const VARIANTS = {
  sandbox: { title: "AI Sandbox", subtitle: "Configure model, prompt and controls, then run a governed AI experiment on synthetic data.", sys: "You are a helpful enterprise AI assistant operating in a governed sandbox.", ph: "Ask the AI to draft, analyse or decide something…" },
  prompt: { title: "Prompt Lab", subtitle: "Test and refine prompts with different models. Observe security and governance events for each run.", sys: "You are a precise assistant. Follow the user's prompt exactly.", ph: "Enter the prompt you want to test…" },
  rag: { title: "RAG Lab", subtitle: "Simulate retrieval-augmented generation grounded in demo knowledge sources.", sys: "You are a RAG assistant. Answer only using retrieved context and cite when unsure. This is a simulation on synthetic knowledge.", ph: "Ask a question that should be grounded in knowledge sources…" },
};

export default function Sandbox({ variant = "sandbox" }) {
  const cfg = VARIANTS[variant];
  const [system, setSystem] = useState(cfg.sys);
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("claude-sonnet-4-6");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!prompt.trim()) return;
    setLoading(true); setRes(null);
    try { const { data } = await api.post("/sandbox/run", { prompt, system_prompt: system, model }); setRes(data); }
    catch { setRes({ output: "Run failed. Please try again.", source: "SIMULATED", latency_ms: 0, security_events: [], governance_events: [] }); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <PageHeader title={cfg.title} subtitle={cfg.subtitle} actions={<StatusBadge value="SANDBOX" />} />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">System Prompt</label>
          <textarea data-testid="sandbox-system" value={system} onChange={(e) => setSystem(e.target.value)} rows={3} className="mb-3 w-full rounded-xl border border-border bg-background p-3 text-sm" />
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Prompt</label>
          <textarea data-testid="sandbox-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={5} placeholder={cfg.ph} className="mb-3 w-full rounded-xl border border-border bg-background p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          <div className="flex items-center gap-2">
            <select data-testid="sandbox-model" value={model} onChange={(e) => setModel(e.target.value)} className="rounded-xl border border-border bg-background px-3 py-2 text-sm">
              {MODELS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
            <button data-testid="sandbox-run" onClick={run} disabled={loading} className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2 font-semibold text-primary-foreground disabled:opacity-50">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Run
            </button>
          </div>
        </Card>

        <Card testId="sandbox-output">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold">Output</h3>
            {res && <div className="flex items-center gap-2"><StatusBadge value={res.source} /><ReadAloud context={res.output} testId="ra-sandbox" /></div>}
          </div>
          {loading && <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>}
          {!loading && !res && <p className="py-12 text-center text-sm text-muted-foreground">{cfg.ph}</p>}
          {!loading && res && (
            <>
              <div data-testid="sandbox-text" className="whitespace-pre-wrap rounded-xl border border-border bg-secondary/30 p-3 text-sm leading-relaxed">{res.output}</div>
              <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5" /> Latency: {res.latency_ms}ms · Model: {res.model}</div>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Security Events</div>
                  {(res.security_events || []).map((e, i) => <div key={i} className="flex items-center justify-between py-1 text-sm"><span>{e.check}</span><StatusBadge value={e.status} /></div>)}
                </div>
                <div>
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Governance Events</div>
                  {(res.governance_events || []).map((e, i) => <div key={i} className="flex items-center justify-between py-1 text-sm"><span>{e.check}</span><span className="text-xs text-foreground/80">{e.status}</span></div>)}
                </div>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
